/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { FC, useEffect, useState } from "react"
import { Col, Row, Radio, Button, List, Checkbox } from "@duxton-web/ui"
import { CloseUniversal } from "@duxton-web/icons"
import { shallowEqual, useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { Item, Modifier, ModifierGroup } from "types/models/menu"
import { numberWithCommas, renderModifierGroupRule } from "utils/misc"
import { minorUnitToCurrency } from "utils/currency"
import { useTranslation, useTranslationsMap } from "common/utils/i18n"
import { getCurrencyCodeByCountryCode } from "common/utils/locale"
import { getUserCountry } from "troy-common/utils"
import { itemPreviewStyle } from "../previewStyles"

interface props {
  item?: Item
  modifierGroups?: ModifierGroup[]
  initSelections?: {
    [key: string]: number[]
  }
}

const radioStyle = {
  height: "30px",
  lineHeight: "30px",
}

export const ItemPreview: FC<props> = ({
  item,
  modifierGroups,
  initSelections,
}: props) => {
  const { outlet, outletV2, enableTroyNewMerchantEndpoint } = useSelector(
    (state: AppState) => state?.menu,
    shallowEqual,
  )
  const outletCurrency =
    (enableTroyNewMerchantEndpoint
      ? outletV2?.currency?.code
      : outlet?.currency) || getCurrencyCodeByCountryCode(getUserCountry())
  const [selections, setSelections] = useState(initSelections ?? {})

  const [isBtnEnabled, setBtnEnabled] = useState(false)

  const textMap = useTranslationsMap({
    pickX: "menu.modifierGroup.preview.pickX",
    pickXtoY: "menu.modifierGroup.preview.pickXtoY",
    pickUpToXOptional: "menu.modifierGroup.preview.pickUpToXoptional",
  })

  const greenButtonClass = "item-button-enabled"
  const greyButtonClass = "item-button"

  const buttonClass = isBtnEnabled ? greenButtonClass : greyButtonClass

  const isSingleSelection = (modifierGroup: ModifierGroup) =>
    renderModifierGroupRule(modifierGroup, textMap)?.requireExactly

  const isAllowToProceed = (newSelections: { [key: string]: number[] }) => {
    let allowToProceed = true
    // check user selection with modifier group minimal selection
    modifierGroups?.forEach(modifierGroup => {
      if (
        Object.keys(newSelections ?? {}).find(
          k =>
            k === modifierGroup.modifierGroupID &&
            newSelections[k].length < modifierGroup.selectionRangeMin,
        )
      )
        allowToProceed = false
    })
    return allowToProceed
  }

  const sumModPrice = (selection: { [key: string]: number[] }) => {
    let sum: number = 0
    Object.entries(selection ?? {}).forEach(([, v]) => {
      sum += v.reduce((acc, val) => {
        return acc + val
      }, 0)
    })
    return minorUnitToCurrency(sum, outletCurrency)
  }

  const handleRadioBtn = (modifierGroup: ModifierGroup, price: number) => {
    const newSelections = selections ?? {}
    newSelections[modifierGroup.modifierGroupID] = [price]
    setSelections({ ...newSelections })
    setBtnEnabled(isAllowToProceed(newSelections))
  }

  const handleCheck = (
    checked: boolean,
    modifierGroup: ModifierGroup,
    price: number,
  ) => {
    const newSelections = selections ?? {}
    if (checked) {
      newSelections[modifierGroup.modifierGroupID].push(price)
    } else {
      const index = newSelections[modifierGroup.modifierGroupID].findIndex(
        v => v === price,
      )
      if (index !== -1) {
        newSelections[modifierGroup.modifierGroupID].splice(index, 1)
      }
    }
    setSelections({ ...newSelections })
    setBtnEnabled(isAllowToProceed(newSelections))
  }

  useEffect(() => {
    setBtnEnabled(isAllowToProceed(initSelections!))
    setSelections(initSelections ?? {})
  }, [initSelections])

  return (
    <div css={itemPreviewStyle}>
      {item?.imageURL && (
        <Row className="item-img-wrapper">
          <Col span={24}>
            <img className="item-img" src={item?.imageURL} />
            <CloseUniversal className="close-icon" />
          </Col>
        </Row>
      )}
      <Row className="item-overview">
        <Col span={12} className="item-name">
          {item?.itemName}
        </Col>
        <Col span={12} style={{ textAlign: "right" }}>
          <p className="item-price">{Number(item?.priceInMin ?? 0)}</p>
          <p className="base-price-label">
            {useTranslation("menu.preview.basePrice")}
          </p>
        </Col>
      </Row>
      <Row className="item-description">
        <Col span={24}>
          <p>{item?.description}</p>
        </Col>
      </Row>
      {modifierGroups?.map(modifierGroup => (
        <div className="item-modifier" key={modifierGroup?.modifierGroupID}>
          <Row>
            <Col span={24}>
              <p className="modifier-group-name">
                {modifierGroup?.modifierGroupName} &nbsp;&nbsp;
                <span>
                  {
                    renderModifierGroupRule(modifierGroup, textMap)
                      ?.modifierGroupRule
                  }
                </span>
              </p>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              {isSingleSelection(modifierGroup) ? (
                <Radio.Group
                  onChange={(e: any) =>
                    handleRadioBtn(modifierGroup, e.target.value)
                  }
                >
                  {modifierGroup?.modifiers?.map((modifier: Modifier) => (
                    <Radio
                      style={radioStyle}
                      value={modifier.priceInMin}
                      key={modifier.modifierID}
                    >
                      <div className="modifier-wrapper">
                        <span>{modifier.modifierName}</span>
                        <span>
                          {modifier.priceInMin !== 0
                            ? `+${numberWithCommas(
                                minorUnitToCurrency(
                                  modifier.priceInMin,
                                  outletCurrency,
                                ),
                              )}`
                            : 0}
                        </span>
                      </div>
                    </Radio>
                  ))}
                </Radio.Group>
              ) : (
                <List
                  data-testid="modifier-group-list"
                  itemLayout="horizontal"
                  dataSource={modifierGroup?.modifiers}
                  renderItem={modifier => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={
                          <Checkbox
                            defaultChecked={
                              modifierGroup.selectionRangeMin ===
                              modifierGroup.selectionRangeMax
                            }
                            onChange={(e: any) => {
                              handleCheck(
                                e.target.checked,
                                modifierGroup,
                                modifier.priceInMin,
                              )
                            }}
                            onClick={(e: any) => {
                              e.stopPropagation()
                            }}
                          />
                        }
                        description={
                          <div className="modifier-wrapper">
                            <span>{modifier.modifierName}</span>
                            <span>
                              {modifier.priceInMin !== 0
                                ? `+${numberWithCommas(
                                    minorUnitToCurrency(
                                      modifier.priceInMin,
                                      outletCurrency,
                                    ),
                                  )}`
                                : 0}
                            </span>
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              )}
            </Col>
          </Row>
        </div>
      ))}
      <Row className={buttonClass}>
        <Col span={24}>
          <Button type="primary" block>
            {useTranslation("menu.buttons.addToBasket")} -{" "}
            {numberWithCommas(
              parseFloat(
                (
                  Number(item?.priceInMin ?? 0) + sumModPrice(selections!)
                ).toFixed(2),
              ),
            )}
          </Button>
        </Col>
      </Row>
    </div>
  )
}
